/**
 * Pathology specialty shell.
 *
 * Three-column Aperio HALO-AP layout:
 *   - Left: slide tray (one card per series = one slide, plus the
 *     class palette).
 *   - Centre: pyramid-aware tiled WSI viewport — fetches only the
 *     tiles needed at the current magnification.
 *   - Right: tabbed dock — Annotations · AI overlays · Synoptic report.
 */
import { batch, createEffect, createMemo, createSignal, For, Index, onCleanup, Show, type JSX } from "solid-js";
import type { StudyInstance } from "@viewer/api/client";
import { WSIPyramidViewport } from "../wsi/WSIPyramidViewport";
import { createAnnotationStore, PATHOLOGY_CLASSES, type PathAnno, type PathologyTool } from "../wsi/annotations";
import { IconEdit, IconTrash, IconSave, IconSign, IconCamera,
         IconChevronRight, IconCase, IconSlide, IconPalette,
         IconMicroscope } from "@viewer/ui/Icons";
import { profile } from "@viewer/settings/profile";
import { pdfLabels, fmtDatePdf } from "@viewer/settings/pdfI18n";
import {
  fetchReport, saveReport, finalizeReport, amendReport,
  OverrideRequiredError, NotAssignedError,
  type PathAnnotationWire, type PathStatus,
} from "../wsi/reportApi";
import { AIOverlayPanel, type Ki67Result } from "../wsi/AIOverlayPanel";
import type { AIOverlayLayer } from "../wsi/WSIViewport";
import { SynopticPanel, type SynopticReport } from "../wsi/SynopticPanel";

export interface PathologyShellProps {
  /** DICOM Study UID — the persistence key for the report row. */
  studyUid:     string;
  caseId?:      string;
  patientName?: string;
  /** All instances in the active series — the pyramid viewport treats
   *  them as resolution levels of one slide. */
  slides:       StudyInstance[];
  activeSlideUid: string;
  onPickSlide:  (instanceUid: string) => void;
  onFinalise?:  (report: SynopticReport) => void;
}

type Tab = "ai" | "report";

export function PathologyShell(p: PathologyShellProps) {
  const store = createAnnotationStore();
  const [tab, setTab] = createSignal<Tab>("ai");
  const [classId, setClassId] = createSignal(PATHOLOGY_CLASSES[0].id);
  const activeClass = () =>
    PATHOLOGY_CLASSES.find((c) => c.id === classId()) ?? PATHOLOGY_CLASSES[0];

  const [layers, setLayers] = createSignal<AIOverlayLayer[]>([]);
  const [ki67,   setKi67]   = createSignal<Ki67Result | null>(null);

  const annoCount = createMemo(() => store.state().items.length);
  const slideLabel = () => p.slides[0]?.seriesDescription || p.caseId;

  // Handle to the viewport's view-setter. Assigned on mount via
  // onViewportReady and called by the Snapshots row thumbnail
  // click to jump the view back to the captured pan/zoom.
  let viewApi: {
    setView: (v: { panX: number; panY: number; zoom: number }) => void;
    getView: () => { panX: number; panY: number; zoom: number };
  } | null = null;
  let viewportWrapEl: HTMLDivElement | undefined;

  /** Compute a pan/zoom that centres the given annotation in the
   *  viewport at ~60 % fill. Used by AnnotationsTab row clicks so
   *  the reader can jump back to an ROI from anywhere (including
   *  the Synoptic typing area via the floating annotations panel).
   *
   *  For rect/ellipse/ruler: bounds = (a, b).
   *  For polygon/freehand:   bounds of the point cloud.
   *  For point/text:         just centre, keep a comfortable zoom.
   */
  function navigateToAnnotation(a: PathAnno) {
    if (!viewApi) return;
    let minX = 0, minY = 0, maxX = 0, maxY = 0;
    let hasBounds = true;
    if (a.kind === "rect" || a.kind === "ellipse" || a.kind === "ruler") {
      minX = Math.min(a.a.x, a.b.x); maxX = Math.max(a.a.x, a.b.x);
      minY = Math.min(a.a.y, a.b.y); maxY = Math.max(a.a.y, a.b.y);
    } else if (a.kind === "polygon" || a.kind === "freehand") {
      const pts = a.points;
      if (!pts.length) return;
      minX = maxX = pts[0].x; minY = maxY = pts[0].y;
      for (const q of pts) {
        if (q.x < minX) minX = q.x; if (q.x > maxX) maxX = q.x;
        if (q.y < minY) minY = q.y; if (q.y > maxY) maxY = q.y;
      }
    } else if (a.kind === "point" || a.kind === "text") {
      // A point has no extent — fall back to a "comfortable"
      // target zoom (0.5 = half-resolution, pathology convention).
      viewApi.setView({ panX: a.at.x, panY: a.at.y, zoom: 0.5 });
      return;
    } else {
      hasBounds = false;
    }
    if (!hasBounds) return;
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const w = Math.max(1, maxX - minX);
    const h = Math.max(1, maxY - minY);
    // Preserve the reader's current magnification. Pathologists
    // label at a specific zoom (10× for tumour-scanning, 40× for
    // nuclear scoring) — jumping to a fit-to-viewport zoom resets
    // that working magnification and forces them to reset it. Only
    // zoom OUT when the annotation is too large to fit at the
    // current zoom (e.g. a full-section ROI clicked while at 40×).
    const cv = document.querySelector("canvas") as HTMLCanvasElement | null;
    const viewportW = cv?.clientWidth ?? 900;
    const viewportH = cv?.clientHeight ?? 700;
    let z = viewApi.getView().zoom;
    const fitZ = Math.min((viewportW * 0.8) / w, (viewportH * 0.8) / h);
    if (fitZ < z) z = Math.max(0.05, fitZ);
    viewApi.setView({ panX: cx, panY: cy, zoom: z });
  }

  // ---------------- Report persistence (draft / finalized / amended) ----------------
  // Mirror of the endoscopy flow. Each logged-in user has their own
  // per-study copy. Loads on mount, falls back through own row →
  // latest other user's finalized → blank template. Save/finalize/
  // amend route to /viewer-api/path/*. Mutations audit-logged.
  const [reportStatus, setReportStatus] = createSignal<PathStatus>("draft");
  const [seededFrom,   setSeededFrom]   = createSignal<string | null>(null);
  const [finalizedAt,  setFinalizedAt]  = createSignal<string | null>(null);
  const [finalizedBy,  setFinalizedBy]  = createSignal<string | null>(null);
  const [saveState,    setSaveState]    = createSignal<"idle" | "saving" | "saved" | "error">("idle");
  const [synopticBody, setSynopticBody] = createSignal<Record<string, any>>({});

  /** Snapshot the client-side annotation store into the server wire
   *  shape. Geometry stays as-is (jsonb on the DB side) so new tool
   *  shapes don't need a migration. */
  function annotationsWire(): PathAnnotationWire[] {
    const out: PathAnnotationWire[] = [];
    for (const a of store.state().items) {
      let geometry: any;
      switch (a.kind) {
        case "rect":
        case "ellipse":
        case "ruler":     geometry = { a: a.a, b: a.b }; break;
        case "polygon":
        case "freehand":  geometry = { points: a.points }; break;
        case "point":     geometry = { at: a.at }; break;
        case "text":      geometry = { at: a.at, text: a.text }; break;
        case "snapshot":  geometry = {
          dataUrl: a.dataUrl,
          panX: a.panX, panY: a.panY, zoom: a.zoom,
          include: a.include, captureAt: a.captureAt,
        }; break;
      }
      out.push({
        kind: a.kind as PathologyTool,
        label: a.label ?? null,
        color: a.color,
        source: a.source ?? "human",
        instance_uid: p.slides[0]?.instanceUid ?? null,
        geometry,
      });
    }
    return out;
  }

  /** Hydrate the client store + synoptic body from the server wire. */
  function applyServerReport(r: Awaited<ReturnType<typeof fetchReport>>) {
    if (!r) return;
    batch(() => {
      setReportStatus(r.status);
      setSeededFrom(r.seededFrom ?? null);
      setFinalizedAt(r.finalizedAt ?? null);
      setFinalizedBy(r.finalizedBy ?? null);
      setSynopticBody(r.body ?? {});
      // Rebuild the annotation store — clear then add. We preserve
      // the server's annotations as-is; reconstruction by kind uses
      // the geometry payload.
      store.clear();
      for (const a of r.annotations ?? []) {
        const g = a.geometry ?? {};
        const base = {
          id: crypto.randomUUID(),
          color: a.color ?? "#a371f7",
          label: a.label ?? undefined,
          source: a.source ?? "human",
        };
        switch (a.kind) {
          case "rect":     store.add({ ...base, kind: "rect",     a: g.a,     b: g.b }     as PathAnno); break;
          case "ellipse":  store.add({ ...base, kind: "ellipse",  a: g.a,     b: g.b }     as PathAnno); break;
          case "ruler":    store.add({ ...base, kind: "ruler",    a: g.a,     b: g.b }     as PathAnno); break;
          case "polygon":  store.add({ ...base, kind: "polygon",  points: g.points ?? [] } as PathAnno); break;
          case "freehand": store.add({ ...base, kind: "freehand", points: g.points ?? [] } as PathAnno); break;
          case "point":    store.add({ ...base, kind: "point",    at: g.at } as PathAnno); break;
          case "text":     store.add({ ...base, kind: "text",     at: g.at, text: g.text ?? "" } as PathAnno); break;
          case "snapshot": store.add({
            ...base, kind: "snapshot",
            dataUrl:   g.dataUrl ?? "",
            panX:      Number(g.panX ?? 0),
            panY:      Number(g.panY ?? 0),
            zoom:      Number(g.zoom ?? 1),
            include:   g.include ?? true,
            captureAt: Number(g.captureAt ?? Date.now()),
          } as PathAnno); break;
        }
      }
    });
  }

  /**
   * Senior-pathologist override prompt (Phase J).
   *
   * On every report write the server applies an assignee gate.
   * Non-assignees holding `reports.override` (admin or
   * senior_pathologist) get a 400 `override_reason_required`
   * response on first attempt. We catch it, prompt for a written
   * justification (≥10 chars; CAP ANP.12150 + 21 CFR Part 11
   * §11.10(g)) and retry with `override_reason` set. The reason
   * lands in the `report.override_*` audit row so the override is
   * reviewable in the admin Audit log.
   *
   * `kind` describes the action being attempted, used in the
   * prompt copy so the reader understands what they're doing.
   */
  async function withOverridePrompt<T>(
    kind: "save the draft" | "finalize the report" | "amend the report",
    op: (overrideReason?: string) => Promise<T>,
  ): Promise<T> {
    try {
      return await op();
    } catch (e) {
      if (e instanceof OverrideRequiredError) {
        const reason = window.prompt(
          `This case is not assigned to you. As a senior pathologist or admin you can ${kind} ` +
          `with a written justification, which will be recorded in the audit trail.\n\n` +
          `Reason for override (≥ 10 characters):`,
          ""
        );
        if (!reason || reason.trim().length < 10) {
          throw new Error("override_cancelled");
        }
        return await op(reason.trim());
      }
      if (e instanceof NotAssignedError) {
        alert("This case is not assigned to you, and your role does not allow override. " +
              "Ask the admin to assign it to you.");
        throw e;
      }
      throw e;
    }
  }

  async function doSaveDraft() {
    setSaveState("saving");
    try {
      const r = await withOverridePrompt("save the draft",
        (reason) => saveReport(p.studyUid, { body: synopticBody(), annotations: annotationsWire() }, reason));
      applyServerReport(r);
      setSaveState("saved");
      setTimeout(() => { if (saveState() === "saved") setSaveState("idle"); }, 2000);
    } catch (e) {
      console.error("[path/save]", e);
      setSaveState("error");
    }
  }
  async function doFinalize() {
    setSaveState("saving");
    try {
      // Save first (with override if needed) then finalize. Both
      // calls share the same override reason if the operator
      // entered one.
      let overrideReason: string | undefined;
      const saveOnce = async (reason?: string) => {
        overrideReason = reason ?? overrideReason;
        return saveReport(p.studyUid, { body: synopticBody(), annotations: annotationsWire() }, reason);
      };
      await withOverridePrompt("finalize the report", saveOnce);
      const r = await finalizeReport(p.studyUid, overrideReason);
      applyServerReport(r);
      setSaveState("saved");
      setTimeout(() => { if (saveState() === "saved") setSaveState("idle"); }, 2000);
    } catch (e) {
      console.error("[path/finalize]", e);
      setSaveState("error");
    }
  }
  async function doAmend() {
    try {
      const r = await withOverridePrompt("amend the report",
        (reason) => amendReport(p.studyUid, reason));
      applyServerReport(r);
    } catch (e) {
      console.error("[path/amend]", e);
    }
  }

  /** Professional PDF export — opens a printable HTML document in
   *  a new tab with the synoptic report + every "include"-checked
   *  illustration embedded inline. Layout follows the standard
   *  surgical-pathology report convention: letterhead with
   *  organisation name + logo, patient block, section stack
   *  (specimen / clinical history / macroscopic / microscopic /
   *  diagnosis / comments), annotation summary, illustrations
   *  grid, and a signature block with credentials + signed date.
   *
   *  All organisation + signer details come from the user's
   *  Profile in Settings (localStorage). Falls back to sensible
   *  blanks when the profile hasn't been filled in — the report
   *  still renders; the reader just sees "—" where their org
   *  didn't populate a field. */
  function exportPDF() {
    // FDA / HIPAA audit: every PHI export is a reportable event
    // (21 CFR Part 11 §11.10(e) + HIPAA §164.312(b)(1)). Fire-and-
    // forget — the audit log is best-effort, a failure here must
    // not block the clinical action.
    const authRaw = sessionStorage.getItem("vpd.auth.v1");
    const accessToken = authRaw ? JSON.parse(authRaw).accessToken : null;
    if (accessToken) {
      fetch("/viewer-api/audit/emit", {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({
          kind: "report.export_pdf",
          object_ref: p.studyUid,
          patient_mrn: p.caseId,
          study_uid:   p.studyUid,
          metadata: { specialty: "pathology", status: reportStatus() },
        }),
      }).catch(() => { /* best-effort */ });
    }
    const prof = profile();
    // Pull report chrome strings from the user's language (EN/VI)
    // so the letterhead, section titles, signature block, and
    // footer all localize together. Values entered by the reader
    // — diagnosis, microscopic description, specimen — stay in
    // whatever language they were typed in.
    const L  = pdfLabels(prof.language);
    const b = synopticBody();
    const snaps = store.state().items.filter(
      (a): a is PathAnno & { kind: "snapshot" } => a.kind === "snapshot"
    ).filter((s) => s.include !== false);
    const annos = store.state().items.filter((a) => a.kind !== "snapshot");

    const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]!));
    const fmtDate = (iso?: string | null) => iso
      ? fmtDatePdf(iso, prof.language)
      : fmtDatePdf(new Date().toISOString(), prof.language);
    const field = (label: string, val: any) => val
      ? `<div class="field"><div class="field-label">${esc(label)}</div><div class="field-body">${esc(String(val))}</div></div>` : "";

    // Signer — prefer profile override if set, else whoever
    // actually finalized, else current auth display name, else "—".
    const signer = prof.overrideSignerFromProfile && prof.displayName
      ? [prof.displayName, prof.credentials].filter(Boolean).join(", ")
      : (finalizedBy() ?? prof.displayName ?? "—");
    const signerTitle = prof.title || (prof.overrideSignerFromProfile ? "" : L.reporting_pathologist);
    const statusLabel = reportStatus() === "draft"    ? L.status_draft
                      : reportStatus() === "finalized" ? L.status_finalized
                      : L.status_amended;

    const html = `<!doctype html><html><head>
      <meta charset="UTF-8" />
      <title>Pathology report — ${esc(p.caseId ?? "")}</title>
      <style>
        @page { margin: 18mm 18mm 20mm; }
        body { font-family: Georgia, "Times New Roman", serif; color: #0f172a; margin: 0;
               font-size: 11pt; line-height: 1.45; }
        .sheet { max-width: 180mm; margin: 0 auto; padding: 8mm 0; }
        .letterhead { display: flex; align-items: center; gap: 16px; border-bottom: 2px solid #0f7a49; padding-bottom: 10px; }
        .letterhead img.logo { max-height: 22mm; max-width: 60mm; object-fit: contain; }
        .letterhead .org { flex: 1; }
        .org-name { font-size: 16pt; font-weight: 700; color: #0f172a; margin: 0; }
        .org-dept { font-size: 12pt; color: #0f7a49; margin: 2px 0 4px; }
        .org-meta { font-size: 9pt; color: #475569; }
        h1.doctitle { font-size: 14pt; font-weight: 700; letter-spacing: 1px; text-transform: uppercase;
                      text-align: center; margin: 14px 0 10px; color: #0f172a; }
        table.patient-meta { width: 100%; border-collapse: collapse; margin: 0 0 10px; font-size: 10pt; }
        table.patient-meta th, table.patient-meta td { border-bottom: 1px solid #e2e8f0; padding: 4px 6px; vertical-align: top; }
        table.patient-meta th { text-align: left; font-weight: 600; color: #475569; width: 20%;
                                background: #f8fafc; text-transform: uppercase; font-size: 9pt; letter-spacing: 0.5px; }
        .section-title { font-size: 10pt; font-weight: 700; text-transform: uppercase;
                         letter-spacing: 1px; color: #0f7a49; margin: 14px 0 4px;
                         border-bottom: 1px solid #0f7a49; padding-bottom: 3px; }
        .field { margin: 6px 0; }
        .field-label { font-size: 9pt; text-transform: uppercase; letter-spacing: 0.5px;
                       color: #475569; font-weight: 600; }
        .field-body { white-space: pre-wrap; font-size: 11pt; color: #0f172a; margin-left: 4px; }
        .diagnosis-box { border: 1px solid #0f7a49; background: #f0fdf4; padding: 8px 12px; margin: 6px 0 10px; border-radius: 3px; }
        .diagnosis-box .field-label { color: #0f7a49; }
        .diagnosis-box .field-body { font-weight: 600; }
        ul.annos-list { list-style: none; padding: 0; margin: 6px 0; column-count: 2; column-gap: 16px; }
        ul.annos-list li { font-size: 10pt; margin: 3px 0; break-inside: avoid; }
        ul.icd10-list { list-style: none; padding: 0; margin: 6px 0; }
        ul.icd10-list li { font-size: 10pt; margin: 4px 0; }
        .chip-principal { display: inline-block; margin-left: 6px; padding: 0 5px;
                          background: #0f7a49; color: #fff; border-radius: 3px;
                          font-size: 8.5pt; font-weight: 600; text-transform: uppercase; }
        .kind-pill { display: inline-block; padding: 0 5px; background: #e2e8f0; color: #475569;
                     border-radius: 3px; font-size: 8pt; text-transform: uppercase; margin-right: 4px; font-family: monospace; }
        .illus-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-top: 6px; }
        .illus { break-inside: avoid; border: 1px solid #cbd5e1; padding: 4px; background: #fff; border-radius: 3px; }
        .illus img { width: 100%; display: block; }
        .illus figcaption { font-size: 9pt; color: #475569; padding: 3px 2px 0; }
        .illus .caption-chip { display: inline-block; background: #64748b; color: #fff; padding: 1px 5px;
                               border-radius: 9999px; font-weight: 600; font-size: 8pt; margin-right: 4px; }
        .sig-block { margin-top: 28px; border-top: 1px solid #cbd5e1; padding-top: 10px; page-break-inside: avoid; }
        .sig-row { display: flex; align-items: flex-end; gap: 20px; margin-bottom: 6px; }
        .sig-row .sig-img { max-height: 14mm; max-width: 50mm; }
        .sig-row .sig-line { border-bottom: 1px solid #0f172a; height: 0.1mm; flex: 1; min-width: 60mm; }
        .sig-name { font-weight: 700; font-size: 11pt; }
        .sig-title { font-size: 9pt; color: #475569; }
        .sig-date { font-size: 9pt; color: #475569; margin-top: 4px; }
        footer.report-footer { margin-top: 18px; border-top: 1px solid #e2e8f0; padding-top: 5px;
                               color: #94a3b8; font-size: 8pt; display: flex; justify-content: space-between; }
        .status-${esc(reportStatus())} { display:inline-block; padding: 2px 8px; border-radius:3px;
            font-size: 9pt; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; margin-left: 6px; }
        .status-draft     { background: #f1f5f9; color: #475569; }
        .status-finalized { background: #f0fdf4; color: #0f7a49; }
        .status-amended   { background: #fff7ed; color: #b45309; }
      </style></head><body>
      <div class="sheet">

        <!-- Letterhead -->
        <div class="letterhead">
          ${prof.orgLogoDataUrl ? `<img class="logo" src="${prof.orgLogoDataUrl}" alt="logo" />` : ""}
          <div class="org">
            <div class="org-name">${esc(prof.orgName || "Your Organisation")}</div>
            <div class="org-dept">${esc(prof.orgDepartment || "Department of Pathology")}</div>
            <div class="org-meta">
              ${[prof.orgAddress, prof.orgPhone, prof.orgEmail].filter(Boolean).map(esc).join(" &middot; ")}
            </div>
          </div>
        </div>

        <h1 class="doctitle">${esc(L.title)}
          <span class="status-${esc(reportStatus())}">${esc(statusLabel)}</span>
        </h1>

        <!-- Patient + case meta -->
        <table class="patient-meta">
          <tr>
            <th>${esc(L.patient)}</th><td>${esc(p.patientName ?? "—")}</td>
            <th>${esc(L.report_date)}</th><td>${esc(fmtDate(finalizedAt()))}</td>
          </tr>
          <tr>
            <th>${esc(L.case_id)}</th><td>${esc(p.caseId ?? "—")}</td>
            <th>${esc(L.study_uid)}</th><td style="font-family:monospace;font-size:8pt;">${esc(p.studyUid)}</td>
          </tr>
        </table>

        ${field(L.specimen, b.specimen)}
        ${field(L.laterality, b.laterality)}
        ${field(L.clinical_history, b.clinicalHistory)}
        ${field(L.gross, b.grossDescription)}
        ${field(L.microscopic, b.microscopicDescription)}

        <div class="section-title">${esc(L.tumour_section)}</div>
        ${field(L.histology_type, b.histologyType)}
        ${field(L.grade, b.grade)}
        ${field(L.tumour_size, b.tumorSizeMm)}
        ${field(L.invasion_depth, b.invasionDepth || b.pT)}
        ${field(L.nodes, (b.lnExamined != null && b.lnPositive != null)
          ? `${b.lnPositive ?? "—"} / ${b.lnExamined ?? "—"}` : "")}
        ${field(L.margins, b.marginStatus)}
        ${field(L.lvi, b.lvi)}
        ${field(L.pni, b.pni)}
        ${field(L.ihc_line,
          [b.er && `ER: ${b.er}`, b.pr && `PR: ${b.pr}`, b.her2 && `HER2: ${b.her2}`, b.ki67Pct && `Ki-67: ${b.ki67Pct}%`]
          .filter(Boolean).join("  ·  "))}
        ${field(L.molecular, [b.mmrStatus && `MMR: ${b.mmrStatus}`, b.msiStatus && `MSI: ${b.msiStatus}`,
                               b.kras && `KRAS: ${b.kras}`, b.braf && `BRAF: ${b.braf}`,
                               b.egfr && `EGFR: ${b.egfr}`, b.alk && `ALK: ${b.alk}`,
                               b.ros1 && `ROS1: ${b.ros1}`, b.pdl1TPS && `PD-L1 TPS: ${b.pdl1TPS}%`]
                              .filter(Boolean).join("  ·  "))}
        ${field(L.gleason,
          (b.gleasonPrimary && b.gleasonSecondary)
            ? `${b.gleasonPrimary} + ${b.gleasonSecondary}${b.isupGroup ? `  ·  ISUP ${b.isupGroup}` : ""}`
            : "")}

        <div class="diagnosis-box">
          <div class="field-label">${esc(L.diagnosis)}</div>
          <div class="field-body">${esc(b.diagnosis || "—")}</div>
        </div>

        ${field(L.comment, b.comment || b.comments)}

        <!-- ICD-10-CM diagnosis codes -->
        ${Array.isArray(b.icd10Codes) && b.icd10Codes.length > 0 ? `
          <div class="section-title">${esc(L.icd10)}</div>
          <ul class="icd10-list">
          ${(b.icd10Codes as any[]).map((c) => {
            const principal = c.principal ? `<span class="chip-principal">principal</span>` : "";
            return `<li><span class="kind-pill">${esc(c.code_dotted ?? "")}</span>${esc(c.long_desc ?? "")}${principal}</li>`;
          }).join("")}
          </ul>
        ` : ""}

        <!-- Annotations summary -->
        ${annos.length ? `<div class="section-title">${esc(L.annotations)} (${annos.length})</div>
          <ul class="annos-list">
          ${annos.map((a) => `<li><span class="kind-pill">${a.kind}</span>${esc(a.label ?? "—")}</li>`).join("")}
          </ul>` : ""}

        <!-- Illustrations -->
        ${snaps.length ? `<div class="section-title">${esc(L.illustrations)} (${snaps.length})</div>
          <div class="illus-grid">
          ${snaps.map((s) => {
            const zoomText = `${s.zoom < 1 ? s.zoom.toFixed(2) : s.zoom.toFixed(1)}×`;
            const chip = s.label ? `<span class="caption-chip" style="background:${s.color || "#64748b"}">${esc(s.label)}</span>` : "";
            return `<figure class="illus"><img src="${s.dataUrl}" alt="" />
              <figcaption>${chip}<span>${esc(L.zoom)} ${zoomText}</span></figcaption></figure>`;
          }).join("")}
          </div>` : ""}

        <!-- Signature block -->
        <div class="sig-block">
          <div class="sig-row">
            ${prof.signatureDataUrl ? `<img class="sig-img" src="${prof.signatureDataUrl}" alt="signature" />`
                                     : `<span class="sig-line"></span>`}
          </div>
          <div class="sig-name">${esc(signer)}</div>
          ${signerTitle ? `<div class="sig-title">${esc(signerTitle)}</div>` : ""}
          <div class="sig-date">${reportStatus() === "finalized" && finalizedAt()
              ? `${esc(L.signed_on)} ${fmtDate(finalizedAt())}`
              : `${esc(L.generated_on)} ${fmtDate(new Date().toISOString())}`}</div>
        </div>

        <footer class="report-footer">
          <span>${esc(L.footer_line)}</span>
          <span>${esc(p.studyUid)}</span>
        </footer>
      </div>
      <script>window.onload=()=>setTimeout(()=>window.print(), 300);</script>
      </body></html>`;
    const w = window.open("", "_blank", "width=920,height=1000");
    if (w) { w.document.write(html); w.document.close(); }
  }

  // Load on study change — the fetch is cheap (~200 ms) so we run
  // it once per studyUid and rehydrate. Failures are logged but
  // don't block the UI — the user can still annotate locally and
  // try saving later.
  createEffect(() => {
    const uid = p.studyUid;
    if (!uid) return;
    fetchReport(uid).then(applyServerReport).catch((e) => console.warn("[path/load]", e));
  });

  // Collapsible panels — the right dock and left slide tray each
  // eat ~300 px of horizontal real estate, and pathologists want the
  // centre viewport as wide as possible when they're actually
  // scanning tissue. The state persists across this shell instance;
  // a future revision can move it to localStorage so the user's last
  // choice sticks across study switches.
  //
  // Mobile default: both CLOSED so the viewport is full-bleed. Same
  // signals drive both the desktop hide-column logic AND the mobile
  // overlay-drawer logic (see aside layout classes below).
  const isSmall = () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
  const [trayOpen, setTrayOpen] = createSignal(!isSmall());
  // Folder-tree disclosure state — persisted so the reader returns
  // to the same shape next visit. The "case" branch itself is
  // always open (it's the root node).
  const [slidesOpen,  setSlidesOpen]  = createSignal(
    (() => { try { return localStorage.getItem("vpd.pathology.tree.slides")  !== "0"; } catch { return true; } })());
  const [classesOpen, setClassesOpen] = createSignal(
    (() => { try { return localStorage.getItem("vpd.pathology.tree.classes") !== "0"; } catch { return true; } })());
  createEffect(() => { try { localStorage.setItem("vpd.pathology.tree.slides",  slidesOpen()  ? "1" : "0"); } catch {} });
  createEffect(() => { try { localStorage.setItem("vpd.pathology.tree.classes", classesOpen() ? "1" : "0"); } catch {} });
  const [dockOpen, setDockOpen] = createSignal(!isSmall());

  // ---------- Right-dock width (drag-to-resize) ----------
  //
  // Persist the user's chosen width so a reader who wants a wider
  // synoptic typing area keeps it across reloads + study switches.
  // Clamped to [280, 900] px — narrower than 280 squeezes the
  // tab headers past the "Annotations 1" label; wider than 900
  // starts eating the viewport on a 1440-wide screen. Only
  // active on md+ screens — on mobile the dock is a full-width
  // overlay drawer and resizing isn't meaningful.
  const DOCK_WIDTH_KEY = "vpd.pathology.dockWidth";
  const initialDockWidth = (() => {
    try {
      const raw = localStorage.getItem(DOCK_WIDTH_KEY);
      const n = raw ? parseInt(raw, 10) : 340;
      // Outer cap only — the ResizeObserver below will re-clamp
      // against the viewport wrapper once mounted.
      return Number.isFinite(n) ? Math.max(280, Math.min(900, n)) : 340;
    } catch { return 340; }
  })();
  const [dockWidth, setDockWidth] = createSignal<number>(initialDockWidth);
  createEffect(() => {
    try { localStorage.setItem(DOCK_WIDTH_KEY, String(dockWidth())); } catch { /* ignore */ }
  });

  // Re-clamp dockWidth when the browser window shrinks below what
  // the current dock can sustain. Keeps at least `minView` px of
  // viewport even after the window narrows. Uses a simple
  // window-based formula (nav + tray assumed ≤ 700 px combined on
  // desktop); the tray is collapsible so this is an upper bound.
  if (typeof window !== "undefined") {
    const clamp = () => {
      const cap = Math.max(280, Math.min(900, window.innerWidth - 700 - 380));
      if (dockWidth() > cap) setDockWidth(cap);
    };
    window.addEventListener("resize", clamp);
    onCleanup(() => window.removeEventListener("resize", clamp));
  }

  // Drag state lives outside the signal so the move handler doesn't
  // re-run the component tree on every pixel of movement. Captured
  // on pointerdown, consumed on pointermove, cleared on up/cancel.
  let dockDragStartX = 0;
  let dockDragStartW = 0;
  function onDockResizeDown(e: PointerEvent) {
    if (isSmall()) return;
    e.preventDefault();
    dockDragStartX = e.clientX;
    dockDragStartW = dockWidth();
    const handle = e.currentTarget as HTMLElement;
    handle.setPointerCapture(e.pointerId);
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
  }
  function onDockResizeMove(e: PointerEvent) {
    if (!dockDragStartW) return;
    const delta = dockDragStartX - e.clientX;
    const minView = 380;
    // If the reader wants more dock than the current viewport can
    // give up, collapse the left tray first — it's the widest
    // dispensable chrome and the study picker isn't needed while
    // working in the dock. This lets dock grow freely instead of
    // silently clamping on top of a narrow viewport.
    const wrapW = viewportWrapEl?.getBoundingClientRect().width ?? 900;
    if (delta > 0 && wrapW < minView + 20 && trayOpen()) {
      setTrayOpen(false);
    }
    // Recompute wrap after the possible tray collapse (next frame
    // would have the new value, but for this move we still use the
    // pre-collapse number — a few pixels off is fine and next frame
    // will catch up).
    const wrapNow = viewportWrapEl?.getBoundingClientRect().width ?? 900;
    const maxByView = dockDragStartW + Math.max(0, wrapNow - minView);
    const maxDock   = Math.min(900, Math.max(280, maxByView));
    setDockWidth(Math.max(280, Math.min(maxDock, dockDragStartW + delta)));
  }
  function onDockResizeUp(e: PointerEvent) {
    if (!dockDragStartW) return;
    const handle = e.currentTarget as HTMLElement;
    try { handle.releasePointerCapture(e.pointerId); } catch { /* ok */ }
    dockDragStartW = 0;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }

  // Fullscreen mode. Collapses every chrome element except the slide
  // canvas + overview minimap + the small ⛶/⤢ toggle that restores
  // the chrome. Remembers the tray/dock state before fullscreen so
  // exit-fullscreen puts the reader back where they were.
  const [fullscreen, setFullscreen] = createSignal(false);
  let savedTray = trayOpen(), savedDock = dockOpen();
  function toggleFullscreen() {
    if (!fullscreen()) {
      savedTray = trayOpen(); savedDock = dockOpen();
      setTrayOpen(false); setDockOpen(false);
      setFullscreen(true);
    } else {
      setFullscreen(false);
      setTrayOpen(savedTray); setDockOpen(savedDock);
    }
  }
  // Esc exits fullscreen — same convention as the browser's native
  // Fullscreen API. Other key handlers in the viewport already let
  // Escape pass (Escape there clears annotation strokes, which is
  // still a useful side-effect).
  if (typeof window !== "undefined") {
    const kh = (e: KeyboardEvent) => {
      if (e.key === "Escape" && fullscreen()) { setFullscreen(false); setTrayOpen(savedTray); setDockOpen(savedDock); }
    };
    window.addEventListener("keydown", kh);
    onCleanup(() => window.removeEventListener("keydown", kh));
  }

  return (
    // In fullscreen, break out of the outer StudyViewer layout by
    // pinning to the viewport with `fixed inset-0 z-50`. StudyViewer's
    // own left nav + study list + annotations panel otherwise wrap
    // PathologyShell into a small middle column — that's fine for the
    // normal "panel inside a viewer" layout but makes an in-panel
    // "fullscreen" meaningless because the slide canvas stays tiny.
    // Computed class instead of `classList` so `relative` is actually
    // *removed* when fullscreen flips on — `classList` layers on top
    // of the base class and a stray `relative` would still win via
    // Tailwind emission order.
    <div class={fullscreen()
      ? "fixed inset-0 z-50 flex min-h-0 w-screen h-screen bg-ink-0"
      : "relative flex h-full min-h-0 w-full"}>
      {/* --------------- Mobile backdrop -----------
          Semi-transparent layer behind the overlay drawers at <md.
          Tap to close whichever is open. Desktop never shows this —
          the columns are persistent, not overlaid. */}
      <Show when={(trayOpen() || dockOpen()) && true}>
        <div class="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm md:hidden"
             onClick={() => { setTrayOpen(false); setDockOpen(false); }} />
      </Show>
      {/* --------------- Slide tray ---------------
          Desktop: persistent left column, `hidden` when collapsed.
          Mobile (<md): fixed overlay drawer, slides in from left.
          Same trayOpen signal drives both — its meaning shifts with
          the breakpoint: "is the column visible?" on desktop,
          "is the overlay open?" on mobile. */}
      <Show when={!trayOpen() && !fullscreen()}>
        <button type="button"
                class="absolute left-0 top-1/2 z-10 -translate-y-1/2 rounded-r border border-l-0 border-ink-300 bg-ink-100/95 px-1 py-3 text-[11px] text-ink-700 backdrop-blur hover:bg-ink-200 hover:text-white"
                title="Show slide tray"
                onClick={() => setTrayOpen(true)}>»</button>
      </Show>
      <aside class="fixed md:static inset-y-0 left-0 z-40 md:z-0 flex w-[85%] xs:w-[70%] md:w-60 max-w-[320px] md:max-w-none shrink-0 flex-col border-r border-ink-300 bg-ink-100 transition-transform md:transition-none shadow-panel md:shadow-none"
             classList={{
               "-translate-x-full": !trayOpen(),
               "translate-x-0": trayOpen(),
               "md:hidden": !trayOpen(),
               "md:translate-x-0": true,
             }}>
        {/* Folder-tree header */}
        <div class="flex items-center justify-between border-b border-ink-300 px-2.5 py-2 text-[10px] uppercase tracking-wider text-ink-700">
          <span class="flex items-center gap-1.5">
            <span class="h-3.5 w-3.5 text-brand"><IconMicroscope /></span>
            <span>pathology</span>
          </span>
          <button type="button"
                  class="text-[12px] text-ink-700 hover:text-white"
                  title="Collapse tray"
                  onClick={() => setTrayOpen(false)}>«</button>
        </div>
        {/* Single scrollable tree */}
        <div class="flex-1 overflow-auto px-1.5 py-2 text-[11px]">
          {/* Case branch (always expanded) */}
          <TreeBranch open={true} setOpen={() => {}}
                      icon={<IconCase />}
                      label={<><span class="text-ink-700">case</span>
                               <span class="ml-1.5 font-mono text-white">{p.caseId}</span></>}>
            {/* Slides sub-branch */}
            <TreeBranch open={slidesOpen()} setOpen={setSlidesOpen}
                        icon={<IconSlide />}
                        label={<><span class="text-ink-700">slides</span>
                                 <span class="ml-1.5 font-mono text-ink-900">{p.slides.length > 0 ? 1 : 0}</span></>}>
              <div class="mx-2 mb-1.5 rounded border border-brand bg-brand/15 px-2 py-1.5">
                <div class="mb-0.5 flex items-center gap-1.5">
                  <span class="chip-brand !text-[9px]">{p.slides[0]?.modality || "SM"}</span>
                  <span class="truncate text-[11px] text-white">{slideLabel() || "Active slide"}</span>
                </div>
                <div class="text-[10px] text-ink-700">
                  pyramid · <span class="font-mono text-ink-900">{p.slides.length}</span> levels
                </div>
              </div>
            </TreeBranch>
            {/* Class palette sub-branch */}
            <TreeBranch open={classesOpen()} setOpen={setClassesOpen}
                        icon={<IconPalette />}
                        label={<><span class="text-ink-700">classes</span>
                                 <span class="ml-1.5 font-mono text-white">{activeClass().label}</span></>}>
              <div class="mx-2 mb-1 flex flex-wrap gap-1">
                <For each={PATHOLOGY_CLASSES}>{(c) => (
                  <button type="button"
                          class="flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] transition"
                          classList={{
                            "border-brand bg-brand/20 text-white": classId() === c.id,
                            "border-ink-300 text-ink-800 hover:text-white": classId() !== c.id,
                          }}
                          title={`${c.label} (${c.shortcut ?? ""})`}
                          onClick={() => setClassId(c.id)}>
                    <span class="h-2 w-2 rounded-sm" style={{ "background-color": c.color }} />
                    {c.label}
                  </button>
                )}</For>
              </div>
            </TreeBranch>
          </TreeBranch>
        </div>
      </aside>

      {/* --------------- WSI viewport --------------- */}
      {/* Our own pyramid viewport. We tried `dicom-microscopy-viewer`
          (see the git history around `17-wsi-viewer-redesign.md`) but
          that library expects a full DICOM-WSI metadata payload
          (SharedFunctionalGroupsSequence · PixelMeasuresSequence ·
          OpticalPathSequence · ImagedVolumeWidth / Height / Depth ·
          PlanePositionSlide · FrameOfReference …) that our ingest
          pipeline does not produce today, and its pyramid math hits
          an unbounded recursion — "RangeError: Maximum call stack
          size exceeded" — when any of those are missing. The
          long-term plan is to widen our ingest to emit the full
          metadata set and reintroduce DMV; for the current demo the
          smaller bespoke viewport running against our
          range-read-backed /metadata + /frames/n path is enough and
          ships in 318 KB instead of 2.9 MB. */}
      <div ref={viewportWrapEl} class="relative flex min-w-0 flex-1">
        <WSIPyramidViewport
          instances={p.slides}
          slideLabel={slideLabel()}
          store={store}
          activeClass={activeClass()}
          classes={PATHOLOGY_CLASSES}
          rightReserve={dockOpen() && !isSmall() && !fullscreen() ? dockWidth() : 0}
          fullscreen={fullscreen()}
          onToggleFullscreen={toggleFullscreen}
          onSnapshot={(snap) => {
            // Each snapshot is a first-class annotation with kind=
            // 'snapshot' — lets it persist + reload through the
            // same path_annotations table as the drawn shapes.
            // Label defaults to the currently-active class name
            // (e.g. "Tumor", "Stroma"); the inline editor can
            // override it.
            store.add({
              id: crypto.randomUUID(),
              kind: "snapshot",
              color: activeClass().color,
              label: activeClass().label,
              source: "human",
              dataUrl: snap.dataUrl,
              panX: snap.panX,
              panY: snap.panY,
              zoom: snap.zoom,
              include: true,
              captureAt: Date.now(),
            } as PathAnno);
          }}
          onViewportReady={(api) => { viewApi = api; }}
          onNavigateTo={(a) => navigateToAnnotation(a)}
        />
      </div>

      {/* --------------- Right dock --------------- */}
      <Show when={!dockOpen() && !fullscreen()}>
        <button type="button"
                class="absolute right-0 top-1/2 z-10 -translate-y-1/2 rounded-l border border-r-0 border-ink-300 bg-ink-100/95 px-1 py-3 text-[11px] text-ink-700 backdrop-blur hover:bg-ink-200 hover:text-white"
                title="Show AI Assistance / Report panel"
                onClick={() => setDockOpen(true)}>«</button>
      </Show>
      {/* Right dock — desktop width is user-resizable via the
          handle pinned to the left edge (see below). On mobile the
          aside becomes a full-width overlay drawer and `style.width`
          is dropped, handing control back to the Tailwind w-[90%]
          class so the responsive layout still works. */}
      <aside class="relative fixed md:static inset-y-0 right-0 z-40 md:z-0 flex w-[90%] xs:w-[75%] shrink-0 flex-col border-l border-ink-300 bg-ink-100 transition-transform md:transition-none shadow-panel md:shadow-none"
             style={isSmall() ? undefined : { width: `${dockWidth()}px` }}
             classList={{
               "translate-x-full": !dockOpen(),
               "translate-x-0": dockOpen(),
               "md:hidden": !dockOpen(),
             }}>
        {/* Drag handle — 6 px wide invisible hit-zone on the left
            edge, with a visible 2 px accent stripe on hover so the
            reader gets a discoverability cue. Pointer events flow
            through onPointerDown → onDockResizeMove → Up; captured
            on the handle element so dragging past the edge still
            fires move events. Only active on md+ where the aside
            is in-flow and resizing is meaningful. */}
        <Show when={!isSmall()}>
          <div class="absolute top-0 bottom-0 -left-[3px] z-50 w-[6px] cursor-ew-resize group"
               title="Drag to resize panel"
               onPointerDown={onDockResizeDown}
               onPointerMove={onDockResizeMove}
               onPointerUp={onDockResizeUp}
               onPointerCancel={onDockResizeUp}>
            <div class="absolute top-0 bottom-0 left-[2px] w-[2px] bg-transparent group-hover:bg-brand/60 transition-colors" />
          </div>
        </Show>
        <div class="flex border-b border-ink-300">
          <TabBtn active={tab() === "ai"}     onClick={() => setTab("ai")}>AI Assistance</TabBtn>
          <TabBtn active={tab() === "report"} onClick={() => setTab("report")}>Report</TabBtn>
          <button type="button"
                  class="flex w-8 shrink-0 items-center justify-center border-l border-ink-300 text-[12px] text-ink-700 hover:text-white"
                  title="Collapse panel"
                  onClick={() => setDockOpen(false)}>»</button>
        </div>
        <div class="flex min-h-0 flex-1 flex-col">
          <Show when={tab() === "ai"}>
            <AIOverlayPanel pixels={null}
                            layers={layers()}
                            setLayers={setLayers}
                            onKi67={(r) => setKi67(r)} />
          </Show>
          <Show when={tab() === "report"}>
            {/* Synoptic tab stacks the template-driven form + an
                Illustrations section that shows the reader's
                snapshots. Snapshots are part of the report (not
                the annotation overlay), so they live here — the
                reader toggles "in PDF" per-shot and the exported
                report embeds them in the order they were captured.
                Both sections share the flex column so the scroll
                region covers everything below the tab header. */}
            <div class="flex min-h-0 flex-1 flex-col overflow-auto">
              <SynopticPanel caseId={p.caseId}
                             patientName={p.patientName}
                             annotationCount={annoCount()}
                             aiKi67Pct={ki67()?.indexPct ?? null}
                             body={synopticBody()}
                             // onBodyChange receives the FULL body,
                             // not a patch — switching templates
                             // prunes fields, so a naive spread-merge
                             // here would leak stale organ fields.
                             onBodyChange={(full) => setSynopticBody(full)}
                             detectFrom={[
                               p.patientName,
                               p.caseId,
                               p.slides[0]?.seriesDescription,
                             ]} />
              <SnapshotsSection items={store.state().items.filter((a) => a.kind === "snapshot") as any[]}
                                onPatch={(id, patch) => store.replace(id, patch)}
                                onRemove={(id) => store.remove(id)}
                                onRestoreView={(v) => viewApi?.setView(v)} />
            </div>
          </Show>
        </div>
        {/* ===== Report-persistence footer =====
            Always-visible across all three tabs. Status badge on the
            left + draft/finalize/amend buttons on the right, same
            flow as the endoscopy shell's footer. */}
        <div class="border-t border-ink-300 p-2">
          <div class="mb-2 flex items-center gap-2 text-[10px]">
            <span class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider"
                  classList={{
                    "bg-ink-200 text-ink-800 ring-1 ring-ink-300": reportStatus() === "draft",
                    "bg-brand/15 text-brand ring-1 ring-brand":    reportStatus() === "finalized",
                    "bg-warn/15 text-warn ring-1 ring-warn":       reportStatus() === "amended",
                  }}>
              {reportStatus()}
            </span>
            <Show when={reportStatus() === "finalized" && finalizedAt()}>
              <span class="truncate text-ink-700">
                {finalizedBy()} · {new Date(finalizedAt()!).toLocaleString()}
              </span>
            </Show>
            <Show when={reportStatus() === "draft" && seededFrom()}>
              <span class="text-[9px] italic text-ink-700">seeded from {seededFrom()}</span>
            </Show>
            <span class="ml-auto text-[9px] font-mono">
              <Show when={saveState() === "saving"}><span class="text-brand">saving…</span></Show>
              <Show when={saveState() === "saved"}><span class="text-brand">✓ saved</span></Show>
              <Show when={saveState() === "error"}><span class="text-danger">save failed</span></Show>
            </span>
          </div>
          <Show when={reportStatus() !== "finalized"} fallback={
            <div class="flex gap-1.5">
              <button class="inline-flex flex-1 items-center justify-center gap-2 rounded border border-warn bg-warn/10 px-3 py-1.5 text-[11px] font-medium text-warn hover:bg-warn hover:text-white"
                      title="Unlock this finalized report for further edits"
                      onClick={doAmend}>
                <span class="h-4 w-4"><IconEdit /></span>
                Amend report
              </button>
              <button class="inline-flex items-center justify-center gap-1.5 rounded border border-ink-300 bg-ink-200 px-3 py-1.5 text-[11px] font-medium text-ink-900 hover:bg-ink-100"
                      title="Open printable PDF of this report"
                      onClick={exportPDF}>
                <span class="h-4 w-4"><IconCamera /></span>
                PDF
              </button>
            </div>
          }>
            {/* Three actions at the same level — Save draft / Sign +
                finalize are the primary write-path; Export PDF is a
                read-only print convenience that works at every
                status (draft, amended, or unlinkable). */}
            <div class="flex gap-1.5">
              <button class="inline-flex flex-1 items-center justify-center gap-1.5 rounded border border-ink-300 bg-ink-200 px-2 py-1.5 text-[11px] font-medium text-ink-900 hover:bg-ink-100"
                      onClick={doSaveDraft}
                      disabled={saveState() === "saving"}>
                <span class="h-4 w-4"><IconSave /></span>
                Save draft
              </button>
              <button class="inline-flex flex-1 items-center justify-center gap-1.5 rounded bg-brand px-2 py-1.5 text-[11px] font-medium text-white hover:bg-brand-dark"
                      onClick={doFinalize}
                      disabled={saveState() === "saving"}>
                <span class="h-4 w-4"><IconSign /></span>
                Sign + finalize
              </button>
              <button class="inline-flex items-center justify-center gap-1.5 rounded border border-ink-300 bg-ink-200 px-2 py-1.5 text-[11px] font-medium text-ink-900 hover:bg-ink-100"
                      title="Open printable PDF of this report"
                      onClick={exportPDF}>
                <span class="h-4 w-4"><IconCamera /></span>
                PDF
              </button>
            </div>
          </Show>
        </div>
      </aside>
    </div>
  );
}

/**
 * Illustrations section shown at the bottom of the Synoptic tab.
 * Each snapshot is a thumbnail + include-checkbox + label pill +
 * edit + delete. Clicking the image restores the captured view in
 * the slide viewport (pan + zoom). Snapshots marked `include` are
 * embedded in the exported PDF report.
 */
function SnapshotsSection(p: {
  items:    any[];   // (PathAnno & { kind: "snapshot" })[]
  onPatch:  (id: string, patch: any) => void;
  onRemove: (id: string) => void;
  onRestoreView: (v: { panX: number; panY: number; zoom: number }) => void;
}) {
  const [editingId, setEditingId] = createSignal<string | null>(null);
  const includedCount = () => p.items.filter((s) => s.include !== false).length;
  return (
    <section class="border-t border-ink-300 bg-ink-100 p-3 text-[11px]">
      <div class="mb-2 flex items-center gap-2 text-[10px]">
        <span class="font-medium uppercase tracking-wider text-ink-700">illustrations</span>
        <span class="font-mono text-ink-900">{p.items.length}</span>
        <span class="ml-2 text-ink-700">· {includedCount()} in PDF</span>
        <span class="ml-auto text-[9px] text-ink-700">press <kbd>S</kbd> on the slide to capture</span>
      </div>
      <Show when={p.items.length > 0} fallback={
        <div class="rounded border border-dashed border-ink-300 bg-ink-50 p-3 text-center text-[10px] text-ink-700">
          No illustrations yet. Capture a view (press <kbd>S</kbd> or the 📸 button in the slide's tool rail)
          to embed it in the PDF report.
        </div>
      }>
        <Index each={p.items}>{(aGet) => {
          const a = aGet;
          const isEditing = () => editingId() === a().id;
          return (
            <div class="mb-2 rounded border border-ink-300 bg-ink-200 p-1.5">
              <div class="flex gap-2">
                <button type="button"
                        class="relative shrink-0 rounded border-2 border-transparent hover:border-brand"
                        title="Click to restore this view (pan + zoom)"
                        onClick={() => p.onRestoreView({
                          panX: a().panX, panY: a().panY, zoom: a().zoom,
                        })}>
                  <img src={a().dataUrl}
                       class="h-20 w-20 rounded object-cover ring-1 ring-ink-300" />
                  <span class="pointer-events-none absolute bottom-0.5 right-0.5 rounded bg-black/70 px-1 font-mono text-[9px] text-white">
                    {a().zoom < 1 ? a().zoom.toFixed(2) : a().zoom.toFixed(1)}×
                  </span>
                </button>
                <div class="flex min-w-0 flex-1 flex-col gap-1">
                  <div class="flex flex-wrap items-center gap-1">
                    <Show when={a().label} fallback={
                      <span class="text-[10px] italic text-ink-700">no caption</span>
                    }>
                      <span class="inline-flex min-w-0 items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium text-white"
                            style={{ "background-color": a().color ?? "#64748b" }}>
                        <span class="truncate">{a().label}</span>
                      </span>
                    </Show>
                  </div>
                  <div class="mt-auto flex items-center gap-1">
                    <label class="inline-flex items-center gap-1 text-[10px] text-ink-900">
                      <input type="checkbox"
                             class="h-3 w-3"
                             checked={a().include !== false}
                             onChange={(e) => p.onPatch(a().id, { include: (e.currentTarget as HTMLInputElement).checked })} />
                      <span>in PDF</span>
                    </label>
                    <button type="button"
                            class="ml-auto inline-flex h-5 w-5 items-center justify-center rounded border border-ink-300 bg-ink-100 text-ink-700 hover:bg-brand hover:border-brand hover:text-white"
                            title={isEditing() ? "Close editor" : "Edit caption"}
                            onClick={() => setEditingId(isEditing() ? null : a().id)}>
                      <span class="h-3.5 w-3.5"><IconEdit /></span>
                    </button>
                    <button type="button"
                            class="inline-flex h-5 w-5 items-center justify-center rounded border border-ink-300 bg-ink-100 text-ink-700 hover:bg-danger hover:border-danger hover:text-white"
                            title="Remove illustration"
                            onClick={() => p.onRemove(a().id)}>
                      <span class="h-3.5 w-3.5"><IconTrash /></span>
                    </button>
                  </div>
                </div>
              </div>
              <Show when={isEditing()}>
                <div class="mt-1.5 border-t border-ink-300 pt-1.5">
                  <label class="mb-0.5 block text-[9px] uppercase tracking-wider text-ink-700">caption</label>
                  <input type="text"
                         class="w-full rounded border border-ink-300 bg-ink-100 px-1.5 py-0.5 text-[10px] text-ink-900 focus:border-brand focus:outline-none"
                         placeholder="e.g. Tumour at 40× — nuclear pleomorphism"
                         value={a().label ?? ""}
                         onInput={(e) => p.onPatch(a().id, { label: (e.currentTarget as HTMLInputElement).value })} />
                  <div class="mt-1 flex items-center justify-end gap-1">
                    <button type="button"
                            class="inline-flex h-5 items-center rounded border border-brand bg-brand/15 px-2 text-[10px] text-brand hover:bg-brand hover:text-white"
                            onClick={() => setEditingId(null)}>done</button>
                  </div>
                </div>
              </Show>
            </div>
          );
        }}</Index>
      </Show>
    </section>
  );
}

function TabBtn(tp: { active: boolean; onClick: () => void; children: any }) {
  return (
    <button type="button"
            class="flex-1 border-b-2 px-3 py-2 text-center text-[11px] uppercase tracking-wider transition"
            classList={{
              "border-brand text-white":          tp.active,
              "border-transparent text-ink-700 hover:text-white": !tp.active,
            }}
            onClick={tp.onClick}>
      {tp.children}
    </button>
  );
}

function AnnotationsTab(p: {
  items:    PathAnno[];
  activeId: string | null;
  onPick:   (id: string) => void;
  onPatch:  (id: string, patch: Partial<PathAnno>) => void;
  onRemove: (id: string) => void;
  onClear:  () => void;
}) {
  // Which row has its inline editor open. Clicking the pencil
  // toggles it; clicking a different row's pencil closes the
  // previous one (we only show one editor at a time to keep the
  // panel compact).
  const [editingId, setEditingId] = createSignal<string | null>(null);
  return (
    <div class="flex h-full flex-col text-[11px]">
      <div class="flex items-center gap-2 border-b border-ink-300 px-3 py-2">
        <span class="text-ink-700">annotations</span>
        <span class="font-mono text-ink-900">{p.items.length}</span>
        <span class="ml-2 text-[9px] normal-case text-ink-700">click pencil to edit · trash to delete</span>
        <button type="button"
                class="ml-auto btn-ghost !text-[10px]"
                disabled={p.items.length === 0}
                onClick={p.onClear}>
          clear all
        </button>
      </div>
      <div class="flex-1 overflow-auto">
        <Show when={p.items.length > 0} fallback={
          <div class="p-6 text-center text-[11px] text-ink-700">
            No annotations yet.<br />Pick a tool from the right edge of the
            viewport — V / R / O / P / G / . / L / T — and draw on the
            slide. Press <kbd>S</kbd> to capture a snapshot of the
            current view for the report.
          </div>
        }>
          {/* <Index> keys by position, not reference. Critical here:
              every keystroke in the label input calls store.replace()
              which rebuilds the items array with a NEW object for the
              edited row. `<For>` would unmount+remount that row's
              subtree — including the <input> — and the user loses
              focus after every single character (same bug the
              endoscopy still editor had). `<Index>` re-uses the DOM
              per-slot and just updates reactive bindings, so the
              input keeps focus while the annotation mutates. */}
          <Index each={p.items}>{(aGet) => {
            const a = aGet;
            const isEditing = () => editingId() === a().id;
            const isSnapshot = () => a().kind === "snapshot";
            return (
              <div class="border-b border-ink-300 px-2 py-1.5"
                   classList={{ "bg-brand/10": p.activeId === a().id }}>
                {/* Snapshot row — image thumbnail on the left,
                    label + controls on the right. Click the image
                    to restore the captured pan/zoom; the chip in
                    the corner shows the zoom level so the reader
                    can tell a 1× overview from a 40× cell shot.
                    `include` checkbox toggles PDF inclusion. */}
                <Show when={isSnapshot()}>
                  {(() => {
                    const snap = () => a() as PathAnno & { kind: "snapshot" };
                    return (
                      <div class="flex gap-2">
                        <button type="button"
                                class="relative shrink-0 rounded border-2 border-transparent hover:border-brand"
                                title="Restore this view (click to jump pan + zoom)"
                                onClick={() => p.onRestoreView({
                                  panX: snap().panX, panY: snap().panY, zoom: snap().zoom,
                                })}>
                          <img src={snap().dataUrl}
                               class="h-20 w-20 rounded object-cover ring-1 ring-ink-300" />
                          <span class="pointer-events-none absolute bottom-0.5 right-0.5 rounded bg-black/70 px-1 font-mono text-[9px] text-white">
                            {snap().zoom < 1 ? snap().zoom.toFixed(2) : snap().zoom.toFixed(1)}×
                          </span>
                        </button>
                        <div class="flex min-w-0 flex-1 flex-col gap-1">
                          <div class="flex flex-wrap items-center gap-1">
                            <span class="inline-flex shrink-0 items-center rounded-sm border border-ink-300 bg-ink-100 px-1 text-[9px] uppercase tracking-wider text-brand">snapshot</span>
                            <Show when={snap().label} fallback={
                              <span class="text-[10px] italic text-ink-700">no label</span>
                            }>
                              <span class="inline-flex min-w-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium text-white"
                                    style={{ "background-color": snap().color }}>
                                <span class="truncate">{snap().label}</span>
                              </span>
                            </Show>
                          </div>
                          <div class="mt-auto flex items-center gap-1">
                            <label class="inline-flex items-center gap-1 text-[10px] text-ink-900">
                              <input type="checkbox"
                                     class="h-3 w-3"
                                     checked={snap().include}
                                     onChange={(e) => p.onPatch(a().id, { include: (e.currentTarget as HTMLInputElement).checked } as Partial<PathAnno>)} />
                              <span>in PDF</span>
                            </label>
                            <button type="button"
                                    class="ml-auto inline-flex h-5 w-5 items-center justify-center rounded border border-ink-300 bg-ink-100 text-ink-700 hover:bg-brand hover:border-brand hover:text-white"
                                    title={isEditing() ? "Close editor" : "Edit label + class"}
                                    onClick={() => setEditingId(isEditing() ? null : a().id)}>
                              <span class="h-3.5 w-3.5"><IconEdit /></span>
                            </button>
                            <button type="button"
                                    class="inline-flex h-5 w-5 items-center justify-center rounded border border-ink-300 bg-ink-100 text-ink-700 hover:bg-danger hover:border-danger hover:text-white"
                                    title="Remove snapshot"
                                    onClick={() => p.onRemove(a().id)}>
                              <span class="h-3.5 w-3.5"><IconTrash /></span>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </Show>
                {/* Geometric annotation row — same layout as before,
                    shown only when kind !== "snapshot". */}
                <Show when={!isSnapshot()}>
                <div class="flex cursor-pointer items-center gap-1.5"
                     onClick={() => p.onPick(a().id)}>
                  <span class="h-2.5 w-2.5 shrink-0 rounded-sm ring-1 ring-black/30"
                        style={{ "background-color": a().color }} />
                  <span class="shrink-0 font-mono text-[10px] uppercase text-ink-700">{a().kind}</span>
                  <span class="truncate text-ink-900">
                    {a().label && a().label!.length > 0
                      ? a().label
                      : <span class="italic text-ink-700">unlabeled</span>}
                    <Show when={a().source === "ai"}>
                      <span class="ml-1 rounded bg-brand/20 px-1 py-0.5 text-[9px] text-brand">AI</span>
                    </Show>
                  </span>
                  <button type="button"
                          class="ml-auto inline-flex h-5 w-5 items-center justify-center rounded border border-ink-300 bg-ink-100 text-ink-700 hover:bg-brand hover:border-brand hover:text-white"
                          title={isEditing() ? "Close editor" : "Edit label + class"}
                          onClick={(e) => { e.stopPropagation(); setEditingId(isEditing() ? null : a().id); }}>
                    <span class="h-3.5 w-3.5"><IconEdit /></span>
                  </button>
                  <button type="button"
                          class="inline-flex h-5 w-5 items-center justify-center rounded border border-ink-300 bg-ink-100 text-ink-700 hover:bg-danger hover:border-danger hover:text-white"
                          title="Delete annotation"
                          onClick={(e) => { e.stopPropagation(); p.onRemove(a().id); }}>
                    <span class="h-3.5 w-3.5"><IconTrash /></span>
                  </button>
                </div>
                <Show when={isEditing()}>
                  <div class="mt-1.5 space-y-1.5 border-t border-ink-300 pt-1.5">
                    <div class="flex items-center gap-1.5">
                      <span class="w-10 text-[9px] uppercase tracking-wider text-ink-700">label</span>
                      <input type="text"
                             class="flex-1 rounded border border-ink-300 bg-ink-100 px-1.5 py-0.5 text-[10px] text-ink-900 focus:border-brand focus:outline-none"
                             placeholder="Free-text label (e.g. Focal DCIS)"
                             value={a().label ?? ""}
                             onInput={(e) => p.onPatch(a().id, { label: (e.currentTarget as HTMLInputElement).value } as Partial<PathAnno>)} />
                    </div>
                    <div class="flex items-center gap-1.5">
                      <span class="w-10 text-[9px] uppercase tracking-wider text-ink-700">class</span>
                      <div class="flex flex-wrap gap-1">
                        <For each={PATHOLOGY_CLASSES}>{(c) => (
                          <button type="button"
                                  class="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px]"
                                  classList={{
                                    "border-brand bg-brand/20 text-white": a().color === c.color,
                                    "border-ink-300 text-ink-800 hover:text-white": a().color !== c.color,
                                  }}
                                  title={`Re-classify as ${c.label}`}
                                  onClick={() => {
                                    // Clicking a class chip ALWAYS overwrites the
                                    // label with the class name — matches the
                                    // user's expectation that "pick Stroma ⇒
                                    // the label says Stroma". Previously we kept
                                    // the existing label when one was set, which
                                    // made the class-chip click look like a
                                    // no-op in the editor field.
                                    p.onPatch(a().id, { color: c.color, label: c.label } as Partial<PathAnno>);
                                  }}>
                            <span class="h-2 w-2 rounded-sm" style={{ "background-color": c.color }} />
                            {c.label}
                          </button>
                        )}</For>
                      </div>
                    </div>
                    <div class="flex items-center justify-end gap-1">
                      <button type="button"
                              class="inline-flex h-5 items-center rounded border border-brand bg-brand/15 px-2 text-[10px] text-brand hover:bg-brand hover:text-white"
                              onClick={() => setEditingId(null)}>done</button>
                    </div>
                  </div>
                </Show>
                </Show>
                {/* Snapshot editor — simple label input; reuses the
                    same Index-keyed slot so typing doesn't lose
                    focus (same pattern as the geometric editor
                    above). */}
                <Show when={isSnapshot() && isEditing()}>
                  {(() => {
                    const snap = () => a() as PathAnno & { kind: "snapshot" };
                    return (
                      <div class="mt-1.5 space-y-1.5 border-t border-ink-300 pt-1.5">
                        <div class="flex items-center gap-1.5">
                          <span class="w-10 text-[9px] uppercase tracking-wider text-ink-700">label</span>
                          <input type="text"
                                 class="flex-1 rounded border border-ink-300 bg-ink-100 px-1.5 py-0.5 text-[10px] text-ink-900 focus:border-brand focus:outline-none"
                                 placeholder="Caption for this illustration"
                                 value={snap().label ?? ""}
                                 onInput={(e) => p.onPatch(a().id, { label: (e.currentTarget as HTMLInputElement).value } as Partial<PathAnno>)} />
                        </div>
                        <div class="flex items-center gap-1.5">
                          <span class="w-10 text-[9px] uppercase tracking-wider text-ink-700">class</span>
                          <div class="flex flex-wrap gap-1">
                            <For each={PATHOLOGY_CLASSES}>{(c) => (
                              <button type="button"
                                      class="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px]"
                                      classList={{
                                        "border-brand bg-brand/20 text-white": snap().color === c.color,
                                        "border-ink-300 text-ink-800 hover:text-white": snap().color !== c.color,
                                      }}
                                      onClick={() => p.onPatch(a().id, { color: c.color, label: c.label } as Partial<PathAnno>)}>
                                <span class="h-2 w-2 rounded-sm" style={{ "background-color": c.color }} />
                                {c.label}
                              </button>
                            )}</For>
                          </div>
                        </div>
                        <div class="flex items-center justify-end gap-1">
                          <button type="button"
                                  class="inline-flex h-5 items-center rounded border border-brand bg-brand/15 px-2 text-[10px] text-brand hover:bg-brand hover:text-white"
                                  onClick={() => setEditingId(null)}>done</button>
                        </div>
                      </div>
                    );
                  })()}
                </Show>
              </div>
            );
          }}</Index>
        </Show>
      </div>
    </div>
  );
}


/** TreeBranch — VSCode-style folder-tree node.
 *  A left-indented group with a chevron disclosure button. Used to
 *  consolidate the pathology shell's left panel into a single
 *  unified tree (Case · Slides · Classes) instead of three stacked
 *  chrome boxes. */
function TreeBranch(p: {
  open: boolean;
  setOpen: (v: boolean) => void;
  icon: JSX.Element;
  label: JSX.Element;
  children: JSX.Element;
}): JSX.Element {
  return (
    <div class="mb-0.5">
      <button type="button"
              class="group flex w-full items-center gap-1.5 rounded px-1 py-1 text-left hover:bg-ink-200"
              onClick={() => p.setOpen(!p.open)}>
        <span class="grid h-3 w-3 place-items-center text-ink-700 transition-transform"
              classList={{ "rotate-90": p.open }}>
          <IconChevronRight size={12} />
        </span>
        <span class="h-3.5 w-3.5 text-brand">{p.icon}</span>
        <span class="flex-1 text-[11px] leading-tight">{p.label}</span>
      </button>
      <Show when={p.open}>
        <div class="ml-3.5 border-l border-ink-300/50 pl-1 pt-0.5">
          {p.children}
        </div>
      </Show>
    </div>
  );
}
